import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LogoFull } from './Logo';
import DevModeSelector from './DevModeSelector';
import { usePlatformSettings } from '../hooks/usePlatformSettings';

const G = '#004D35';
const FALLBACK_APPLY_URL = 'https://form.typeform.com/to/IuLOXHct';

function HamburgerIcon({ color = G }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 22, height: 2, background: color, borderRadius: 2 }} />
      ))}
    </div>
  );
}

function MobileOverlay({ onClose, navigate, isAuthenticated, user, getDashboardLink, signOut, applyUrl }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleLink = (path) => {
    onClose();
    setTimeout(() => navigate(path), 50);
  };

  const handleAnchor = (hash) => {
    onClose();
    setTimeout(() => {
      if (window.location.pathname === '/') {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = `/${hash}`;
      }
    }, 80);
  };

  const handleApply = () => {
    onClose();
    window.open(applyUrl || FALLBACK_APPLY_URL, '_blank', 'noopener noreferrer');
  };

  const links = [
    { label: 'How it Works', action: () => handleAnchor('#how-it-works') },
    { label: 'Browse Advisors', action: () => handleLink('/browse') },
    { label: 'For Advisors', action: () => handleAnchor('#for-advisors') },
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#fff', zIndex: 200, padding: 24,
      display: 'flex', flexDirection: 'column',
      animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards',
    }}>
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <LogoFull size={28} />
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', fontSize: 24, color: '#888', cursor: 'pointer', lineHeight: 1, padding: 4 }}
          aria-label="Close menu"
        >
          ✕
        </button>
      </div>

      <nav style={{ flex: 1 }}>
        {links.map(({ label, action }) => (
          <button
            key={label}
            onClick={action}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '18px 0', fontSize: 18, fontFamily: 'Georgia, serif',
              fontWeight: 500, color: '#0a1a0f',
              borderBottom: '1px solid #f0ede8', background: 'none',
              border: 'none', borderBottom: '1px solid #f0ede8',
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 32 }}>
        {isAuthenticated && user ? (
          <>
            <button
              onClick={() => handleLink(getDashboardLink())}
              style={{ width: '100%', background: G, color: '#fff', border: 'none', borderRadius: 999, padding: 14, fontSize: 15, fontFamily: 'sans-serif', fontWeight: 700, cursor: 'pointer' }}
            >
              Dashboard
            </button>
            <button
              onClick={async () => { onClose(); await signOut(); navigate('/'); }}
              style={{ width: '100%', background: '#fff', color: '#666', border: '1.5px solid #e0ddd8', borderRadius: 999, padding: 14, fontSize: 15, fontFamily: 'sans-serif', fontWeight: 600, cursor: 'pointer' }}
            >
              Sign Out
            </button>
          </>
        ) : (
          <button
            onClick={() => handleLink('/login')}
            style={{ width: '100%', background: '#fff', color: G, border: `1.5px solid ${G}`, borderRadius: 999, padding: 14, fontSize: 15, fontFamily: 'sans-serif', fontWeight: 700, cursor: 'pointer' }}
          >
            Sign In
          </button>
        )}
        <button
          onClick={handleApply}
          style={{ width: '100%', background: G, color: '#fff', border: 'none', borderRadius: 999, padding: 14, fontSize: 15, fontFamily: 'sans-serif', fontWeight: 700, cursor: 'pointer' }}
        >
          Apply as an Advisor
        </button>
      </div>
    </div>
  );
}

const GlobalNav = ({ page = 'browse' }) => {
  const { user, isAuthenticated, signOut, isDev, setRole } = useAuth();
  const navigate = useNavigate();
  const { settings: platformSettings } = usePlatformSettings();
  const applyUrl = platformSettings.advisor_application_url || FALLBACK_APPLY_URL;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isScrolled, setIsScrolled] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const isHomepage = page === 'homepage';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isHomepage) return;
    const handleScroll = () => setIsScrolled(window.scrollY >= 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomepage]);

  const getDashboardLink = useCallback(() => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'advisor': return '/advisor-portal';
      default: return '/onboarding';
    }
  }, [user]);

  const handleSignOut = async () => {
    setUserDropdownOpen(false);
    await signOut();
    navigate('/');
  };

  const linkColor = isHomepage ? (isScrolled ? '#0a1a0f' : '#ffffff') : '#555';
  const logoMarkBg = isHomepage
    ? (isScrolled ? G : 'rgba(255,255,255,0.18)')
    : G;
  const logoTextColor = isHomepage ? (isScrolled ? G : '#ffffff') : G;

  const navStyle = isHomepage ? {
    position: 'fixed',
    top: 0,
    left: isScrolled && !isMobile ? '50%' : 0,
    right: isScrolled && !isMobile ? 'auto' : 0,
    transform: isScrolled && !isMobile ? 'translateX(-50%)' : 'none',
    maxWidth: isScrolled && !isMobile ? 900 : '100%',
    width: isScrolled && !isMobile ? 'calc(100% - 48px)' : '100%',
    margin: isScrolled && !isMobile ? '12px auto' : 0,
    padding: isMobile
      ? (isScrolled ? '10px 20px' : '16px 24px')
      : (isScrolled ? '10px 24px' : '20px 48px'),
    background: isScrolled ? '#ffffff' : 'transparent',
    borderRadius: isScrolled && !isMobile ? 9999 : 0,
    boxShadow: isScrolled ? '0 4px 24px rgba(0,0,0,0.10)' : 'none',
    zIndex: 50,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  } : {
    position: 'sticky',
    top: 0,
    background: '#fff',
    borderBottom: '1px solid #e8e5e0',
    padding: isMobile ? '14px 20px' : '14px 48px',
    zIndex: 50,
  };

  const mobileSignInColor = isHomepage
    ? (isScrolled ? '#fff' : 'rgba(255,255,255,0.85)')
    : '#fff';
  const mobileSignInBg = isHomepage
    ? (isScrolled ? G : 'transparent')
    : G;
  const mobileSignInBorder = isHomepage
    ? (isScrolled ? 'none' : '1px solid rgba(255,255,255,0.45)')
    : 'none';
  const hamburgerColor = isHomepage
    ? (isScrolled ? G : '#ffffff')
    : G;

  return (
    <>
      {overlayOpen && (
        <MobileOverlay
          onClose={() => setOverlayOpen(false)}
          navigate={navigate}
          isAuthenticated={isAuthenticated}
          user={user}
          getDashboardLink={getDashboardLink}
          signOut={signOut}
          applyUrl={applyUrl}
        />
      )}

      <nav style={navStyle}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <LogoFull
                size={isHomepage ? 32 : 28}
                markBg={logoMarkBg}
                markStroke="#ffffff"
                textColor={logoTextColor}
              />
            </Link>
          </div>

          {isMobile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isAuthenticated && user ? (
                <Link
                  to={getDashboardLink()}
                  style={{
                    background: mobileSignInBg,
                    border: mobileSignInBorder,
                    color: mobileSignInColor,
                    fontWeight: 700, fontSize: 13,
                    padding: '7px 16px', borderRadius: 999,
                    textDecoration: 'none', fontFamily: 'sans-serif',
                    transition: 'all 0.3s ease',
                  }}
                >
                  Dashboard
                </Link>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    background: mobileSignInBg,
                    border: mobileSignInBorder,
                    color: mobileSignInColor,
                    fontWeight: 700, fontSize: 13,
                    padding: '7px 16px', borderRadius: 999,
                    cursor: 'pointer', fontFamily: 'sans-serif',
                    transition: 'all 0.3s ease',
                  }}
                >
                  Sign In
                </button>
              )}
              <button
                onClick={() => setOverlayOpen(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}
                aria-label="Open menu"
              >
                <HamburgerIcon color={hamburgerColor} />
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 28 }} className="hidden md:flex">
                {isHomepage ? (
                  <>
                    <a href="#how-it-works" style={{ color: linkColor, fontWeight: 500, fontSize: 15, textDecoration: 'none', transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>How it Works</a>
                    <Link to="/browse" style={{ color: linkColor, fontWeight: 500, fontSize: 15, textDecoration: 'none', transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>Browse All</Link>
                    <a href="#for-advisors" style={{ color: linkColor, fontWeight: 500, fontSize: 15, textDecoration: 'none', transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>For Advisors</a>
                  </>
                ) : (
                  <>
                    <Link to="/" style={{ fontSize: 14, fontFamily: 'sans-serif', color: '#555', fontWeight: 400, textDecoration: 'none' }}>Home</Link>
                    {page === 'browse' ? (
                      <span style={{ fontSize: 14, fontFamily: 'sans-serif', color: G, fontWeight: 700 }}>Browse All</span>
                    ) : (
                      <Link to="/browse" style={{ fontSize: 14, fontFamily: 'sans-serif', color: '#555', fontWeight: 400, textDecoration: 'none' }}>Browse All</Link>
                    )}
                    <a href={applyUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, fontFamily: 'sans-serif', color: '#555', fontWeight: 400, textDecoration: 'none' }}>Become an Advisor</a>
                  </>
                )}
              </div>

              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                {isDev && <DevModeSelector currentRole={user?.role} onRoleChange={setRole} />}
                {isAuthenticated && user?.role === 'advisor' && (
                  <Link
                    to="/advisor-portal"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', borderRadius: 999,
                      background: G, color: '#fff',
                      fontSize: 13, fontWeight: 700, textDecoration: 'none',
                      fontFamily: 'sans-serif', whiteSpace: 'nowrap',
                    }}
                  >
                    My Portal
                  </Link>
                )}
                {isAuthenticated && user ? (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer' }}
                    >
                      <div style={{ width: 32, height: 32, background: G, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{user.name?.charAt(0) || 'U'}</span>
                      </div>
                      <ChevronDown style={{ width: 16, height: 16, color: linkColor, transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                    </button>
                    {userDropdownOpen && (
                      <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, width: 192, background: '#fff', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', padding: '4px 0', zIndex: 100 }}>
                        <Link to={getDashboardLink()} onClick={() => setUserDropdownOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', color: '#444', textDecoration: 'none', fontSize: 14 }}>
                          <User style={{ width: 16, height: 16 }} /> Dashboard
                        </Link>
                        {user.role === 'admin' && (
                          <Link to="/admin" onClick={() => setUserDropdownOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', color: '#444', textDecoration: 'none', fontSize: 14 }}>
                            <Settings style={{ width: 16, height: 16 }} /> Admin
                          </Link>
                        )}
                        <button onClick={handleSignOut} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', color: '#444', background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', fontSize: 14 }}>
                          <LogOut style={{ width: 16, height: 16 }} /> Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : isHomepage ? (
                  <>
                    <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: isScrolled ? '#444' : 'rgba(255,255,255,0.75)', fontWeight: 500, fontSize: 15, padding: '8px 16px', cursor: 'pointer', transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      Log in
                    </button>
                    <button onClick={() => navigate('/get-started')} style={{ background: isScrolled ? G : 'transparent', border: isScrolled ? 'none' : '1px solid rgba(255,255,255,0.4)', color: '#ffffff', fontWeight: 600, fontSize: 15, padding: '9px 20px', borderRadius: 9999, cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      Get started
                    </button>
                  </>
                ) : (
                  <button onClick={() => navigate('/login')} style={{ background: G, color: '#fff', border: 'none', padding: '9px 22px', borderRadius: 999, fontSize: 14, fontFamily: 'sans-serif', fontWeight: 700, cursor: 'pointer' }}>
                    Sign In
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export default GlobalNav;
